import { t } from 'ember-intl';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import { PhArrowSquareOut } from 'ember-phosphor-icons';
import { Divider } from 'tio-ui/components/utilities';
import type { TOC } from '@ember/component/template-only';
import ExpandableSection from '../expandable-section.gts';
import type TasApplicationModel from 'tio-data/models/tas-application';
import { DrawerHeader, DrawerBody } from 'tio-reimagined/components/drawer';

/**
 * Approver instructions item
 */
export interface ApproverInstructionItem {
  heading: string;
  body: string;
  ordinal: number;
}

/**
 * Employee FAQ item
 */
export interface EmployeeFaqItem {
  heading: string;
  body: string;
  ordinal: number;
}

/**
 * Employer Info drawer content component
 * Displays partner information, approver instructions, and employee FAQs
 */
export interface PartnerInfoDrawerSignature {
  Args: {
    model: TasApplicationModel;
    approverInstructionsItems: ApproverInstructionItem[];
    employeeFaqsItems: EmployeeFaqItem[];
    /**
     * Whether this is being used in the admin app context
     * Determines routing for program info link and visibility of policy link
     */
    isAdminApp?: boolean;
  };
}

const PartnerInfoDrawer: TOC<PartnerInfoDrawerSignature> = <template>
  <DrawerHeader>
    <h2 class="text-lg font-semibold text-gray-800">{{t
        "tuition_assistance.application_approval.partner_information"
      }}</h2>
  </DrawerHeader>

  <DrawerBody>
    <div class="p-6">
      {{#if @model.tasProgramInstance.tasProgramTemplate}}
        {{! Approver Instructions Section }}
        {{#if @approverInstructionsItems.length}}
          <ExpandableSection
            @title={{t "tuition_assistance.application_approval.approver_instructions"}}
            @defaultExpanded={{true}}
            class="mb-8"
          >
            <ul class="list-disc ml-6 space-y-4 text-gray-600 text-base leading-relaxed">
              {{#each @approverInstructionsItems as |item|}}
                <li>{{htmlSafe item.body}}</li>
              {{/each}}
            </ul>
          </ExpandableSection>
          <Divider class="my-4" />
        {{/if}}

        {{! Employee FAQs Section }}
        {{#if @employeeFaqsItems.length}}
          <ExpandableSection
            @title={{t "tuition_assistance.application_approval.employee_faqs"}}
            @defaultExpanded={{true}}
            class="mb-8"
          >
            <div class="space-y-8">
              {{#each @employeeFaqsItems as |faq|}}
                <div class="space-y-2">
                  <h4 class="text-base font-semibold text-gray-700 leading-snug">
                    {{htmlSafe faq.heading}}
                  </h4>
                  <p class="text-base text-gray-600 leading-relaxed">
                    {{htmlSafe faq.body}}
                  </p>
                </div>
              {{/each}}
            </div>
          </ExpandableSection>
        {{/if}}

        {{! Divider }}
        <Divider class="my-8" />

        {{! View Program Information Link }}
        {{#if @isAdminApp}}
          {{! Admin app - link to program-builder }}
          <LinkTo
            @route="authenticated.tuition-assistance.program-builder.program-template"
            @model={{@model.tasProgramInstance.tasProgramTemplate.id}}
            class="flex items-center gap-2 text-teal-600 hover:text-teal-800 cursor-pointer"
            data-test-view-program-info-link
          >
            <span class="font-semibold text-base">
              {{t "tuition_assistance.application_approval.view_program_information"}}
            </span>
            <PhArrowSquareOut @size="20" />
          </LinkTo>
        {{else}}
          {{! Employee app - link to program-templates }}
          <LinkTo
            @route="authenticated.admin.tuition-assistance.program-templates.show"
            @model={{@model.tasProgramInstance.tasProgramTemplate.id}}
            class="flex items-center gap-2 text-teal-600 hover:text-teal-800 cursor-pointer"
            data-test-view-program-info-link
          >
            <span class="font-semibold text-base">
              {{t "tuition_assistance.application_approval.view_program_information"}}
            </span>
            <PhArrowSquareOut @size="20" />
          </LinkTo>
        {{/if}}

        {{! View Policy Link - Only show in tio-admin }}
        {{#if @isAdminApp}}
          <LinkTo
            @route="authenticated.companies.show.policy-information"
            @model={{@model.tasProgramInstance.employee.company.id}}
            class="flex items-center gap-2 text-teal-600 hover:text-teal-800 cursor-pointer mt-4"
            data-test-view-policy-link
          >
            <span class="font-semibold text-base">{{t
                "tuition_assistance.application_approval.view_policy"
              }}</span>
            <PhArrowSquareOut @size="20" />
          </LinkTo>
        {{/if}}
      {{else}}
        <p class="text-gray-500">{{t
            "tuition_assistance.application_approval.no_program_template_data"
          }}</p>
      {{/if}}
    </div>
  </DrawerBody>
</template>;

export default PartnerInfoDrawer;
